import { useEffect, useState, useCallback } from "react";
import parentApi from "@/lib/parentApi";

function urlB64ToUint8Array(base64String) {
  const padding = "=".repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, "+").replace(/_/g, "/");
  const rawData = window.atob(base64);
  return Uint8Array.from([...rawData].map((c) => c.charCodeAt(0)));
}

async function swReady() {
  const existing = await navigator.serviceWorker.getRegistration();
  if (existing) return existing.active ? existing : navigator.serviceWorker.ready;
  await navigator.serviceWorker.register("/service-worker.js");
  return navigator.serviceWorker.ready;
}

export function usePushNotifications() {
  const [status, setStatus] = useState("unsupported"); // unsupported | prompt | enabled | denied
  const supported = "serviceWorker" in navigator && "PushManager" in window && "Notification" in window;

  const check = useCallback(async () => {
    if (!supported) return;
    if (Notification.permission === "denied") { setStatus("denied"); return; }
    try {
      const reg = await swReady();
      const sub = await reg.pushManager.getSubscription();
      if (Notification.permission === "granted" && sub) {
        // keep the backend in sync
        await parentApi.post("/parent/push/subscribe", { subscription: sub.toJSON() }).catch(() => {});
        setStatus("enabled");
      } else {
        setStatus("prompt");
      }
    } catch {
      setStatus("prompt");
    }
  }, [supported]);

  useEffect(() => { check(); }, [check]);

  const enable = useCallback(async () => {
    if (!supported) return false;
    const perm = await Notification.requestPermission();
    if (perm !== "granted") { setStatus(perm === "denied" ? "denied" : "prompt"); return false; }
    try {
      const reg = await swReady();
      const { data } = await parentApi.get("/parent/push/key");
      if (!data.enabled || !data.public_key) return false;
      const sub = await reg.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: urlB64ToUint8Array(data.public_key),
      });
      await parentApi.post("/parent/push/subscribe", { subscription: sub.toJSON() });
      setStatus("enabled");
      return true;
    } catch {
      return false;
    }
  }, [supported]);

  return { status, enable };
}
